/**
 * Alternative Staff Selector Component
 * Displays alternative staff suggestions with match scores and comparison
 */

import React, { useState } from 'react';
import { Card, Button, Badge, LoadingSpinner } from '@tracekit/core';
import { useQuery } from '@tanstack/react-query';
import { getAPIClient } from '@tracekit/core';
import {
  Search,
  CheckCircle,
  AlertCircle,
  XCircle,
  Award,
  TrendingUp,
  User,
  ArrowRight,
  Info,
} from 'lucide-react';

interface Alternative {
  staff_member_id: string;
  staff_member_name: string;
  match_score: number;
  match_status: 'matched' | 'partial_match' | 'no_match';
  explanation: string;
  matched_rules: Array<{
    rule_name?: string;
    message?: string;
    score_contribution?: number;
    weight?: number;
  }>;
}

interface AlternativeStaffSelectorProps {
  requirementId: string;
  projectId: string;
  currentStaffId?: string;
  onSelect?: (staffId: string) => void;
  onCompare?: (staffId: string) => void;
}

export function AlternativeStaffSelector({
  requirementId,
  projectId,
  currentStaffId,
  onSelect,
  onCompare,
}: AlternativeStaffSelectorProps) {
  const [selectedAlternative, setSelectedAlternative] = useState<string | null>(null);

  // Fetch alternatives
  const { data: alternativesData, isLoading } = useQuery<{
    requirement_id: string;
    requirement_details: any;
    alternatives: Alternative[];
    total_alternatives: number;
  }>({
    queryKey: ['staff-alternatives', projectId, requirementId],
    queryFn: async () => {
      const api = getAPIClient();
      const response = await api.get('/api/rfp/staff/alternatives', {
        params: {
          requirement_id: requirementId,
          project_id: projectId,
        },
      });
      return response;
    },
    enabled: !!requirementId && !!projectId,
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'matched':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'partial_match':
        return <AlertCircle className="w-5 h-5 text-yellow-600" />;
      case 'no_match':
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Info className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'matched':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-200';
      case 'partial_match':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200';
      case 'no_match':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-200';
    }
  };

  if (isLoading) {
    return (
      <Card className="p-8">
        <div className="flex items-center justify-center">
          <LoadingSpinner />
          <span className="ml-3 text-gray-600">Finding alternatives...</span>
        </div>
      </Card>
    );
  }

  if (!alternativesData || alternativesData.alternatives.length === 0) {
    return (
      <Card className="p-8">
        <div className="text-center">
          <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-theme-text mb-2">
            No Alternatives Found
          </h3>
          <p className="text-gray-600 dark:text-theme-text-secondary">
            No alternative staff members found matching the requirements.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Requirement Details */}
      {alternativesData.requirement_details && (
        <Card className="p-4 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
          <h3 className="font-semibold mb-2 text-gray-900 dark:text-theme-text">
            Requirement Details
          </h3>
          <div className="text-sm text-gray-700 dark:text-gray-300">
            <div className="font-medium">{alternativesData.requirement_details.title || 'Unknown Requirement'}</div>
            {alternativesData.requirement_details.required_designations && 
             alternativesData.requirement_details.required_designations.length > 0 && (
              <div className="mt-2">
                <span className="font-medium">Required Designations: </span>
                {alternativesData.requirement_details.required_designations.join(', ')}
              </div>
            )}
            {alternativesData.requirement_details.required_expertise && 
             alternativesData.requirement_details.required_expertise.length > 0 && (
              <div className="mt-1">
                <span className="font-medium">Required Expertise: </span>
                {alternativesData.requirement_details.required_expertise.join(', ')}
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Alternatives List */}
      <div>
        <h3 className="font-semibold mb-3 flex items-center gap-2">
          <User className="w-5 h-5" />
          Alternative Staff ({alternativesData.total_alternatives})
        </h3>
        <div className="space-y-3">
          {alternativesData.alternatives.map((alt, idx) => {
            const isSelected = selectedAlternative === alt.staff_member_id;
            const isCurrent = currentStaffId === alt.staff_member_id;
            
            return (
              <Card
                key={idx}
                className={`p-4 border-2 transition-all ${
                  isSelected
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    {getStatusIcon(alt.match_status)}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-semibold text-gray-900 dark:text-theme-text">
                          {alt.staff_member_name}
                        </span>
                        {isCurrent && (
                          <Badge variant="outline" className="text-xs">
                            Current
                          </Badge>
                        )}
                        <Badge className={`text-xs ${getStatusColor(alt.match_status)}`}>
                          {alt.match_status.replace('_', ' ')}
                        </Badge>
                        <div className="ml-auto flex items-center gap-1">
                          <TrendingUp className="w-4 h-4 text-blue-600" />
                          <span className="text-lg font-bold text-blue-600">
                            {(alt.match_score * 100).toFixed(1)}%
                          </span>
                        </div>
                      </div>
                      
                      {alt.explanation && (
                        <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                          {alt.explanation.split('\n')[0]}
                        </p>
                      )}

                      {alt.matched_rules && alt.matched_rules.length > 0 && (
                        <div className="mt-2">
                          <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Matched Rules:
                          </div>
                          <div className="space-y-1">
                            {alt.matched_rules.slice(0, 3).map((rule, ruleIdx) => (
                              <div key={ruleIdx} className="text-xs bg-gray-50 dark:bg-gray-800 rounded px-2 py-1">
                                <span className="font-medium">{rule.rule_name || `Rule ${ruleIdx + 1}`}: </span>
                                <span className="text-gray-600 dark:text-gray-400">
                                  {rule.message || rule.explanation || 'Match found'}
                                </span>
                                {rule.score_contribution !== undefined && (
                                  <span className="text-gray-500 ml-2">
                                    (+{rule.score_contribution.toFixed(1)}%)
                                  </span>
                                )}
                              </div>
                            ))}
                            {alt.matched_rules.length > 3 && (
                              <div className="text-xs text-gray-500">
                                +{alt.matched_rules.length - 3} more rules
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedAlternative(alt.staff_member_id);
                      onCompare?.(alt.staff_member_id);
                    }}
                  >
                    <Info className="w-4 h-4 mr-1" />
                    Compare
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => {
                      setSelectedAlternative(alt.staff_member_id);
                      onSelect?.(alt.staff_member_id);
                    }}
                    className="ml-auto"
                  >
                    Select Alternative
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Help Text */}
      <Card className="p-4 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800 dark:text-blue-300">
            <p className="font-semibold mb-1">Alternative Staff Selection:</p>
            <ul className="list-disc list-inside space-y-1 text-xs">
              <li>Match scores indicate how well staff meet the requirements</li>
              <li>Review matched rules to understand qualification alignment</li>
              <li>Compare alternatives to see detailed qualification differences</li>
              <li>Select an alternative to reassign the requirement</li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
}
